import type {
  CMSGroupParams,
  CMSImageBase,
  CMSLicenseText,
} from 'api/cms/base';
import { schedule } from 'helpers/cms';

type TPromo = {
  start: string;
  end: string;
};

type TournamentGameCMSField = string | null;

export interface ITournamentGame extends CMSGroupParams {
  tournamentGameIds: TournamentGameCMSField;
  tournamentGameCategories: TournamentGameCMSField;
}

export interface IPageVisibilityItem
  extends Omit<CMSGroupParams, 'durationFrom' | 'durationTo'> {
  enable: boolean;
}

export interface IPageVisibilityField {
  fortuneWheel: IPageVisibilityItem | null;
  referralProgram: IPageVisibilityItem | null;
  stardustRush: IPageVisibilityItem | null;
  vip: IPageVisibilityItem | null;
  luckyQuest: IPageVisibilityItem | null;
  loyalty: IPageVisibilityItem | null;
}

export interface IBrandAsset {
  logoDesktop: CMSImageBase | null;
  logoMobile: CMSImageBase | null;
  preloader: CMSImageBase | null;
  animatedLogo: boolean;
  durationFrom: string | null;
  durationTo: string | null;
}

type IConfigContextActions = {};

export interface IConfigContextData {
  quest: TPromo | null;
  mission: TPromo | null;
  tournamentGames: ITournamentGame[];
  isQuestDateValid: boolean;
  isMissionDateValid: boolean;
  footerCopyright: CMSLicenseText;
  pagesVisibility: IPageVisibilityField;
  headlinePromo: TPromo | null;
  isHeadlinePromoDateValid: boolean;
  pumpkinRush: TPromo | null;
  isPumpkinRushDateValid: boolean;
  brandAssets: IBrandAsset[];
}

// Derived in the provider, not returned by the CMS config endpoint
interface IConfigContextDerived {
  activeBrandAsset: IBrandAsset | null;
}

export interface IConfigContext
  extends IConfigContextData,
    IConfigContextDerived,
    IConfigContextActions {}

export const initialValues: IConfigContextData = {
  quest: null,
  mission: null,
  tournamentGames: [],
  isQuestDateValid: false,
  isMissionDateValid: false,
  footerCopyright: {
    textTabique: '',
    textAnjouan: '',
  },
  pagesVisibility: {
    fortuneWheel: null,
    referralProgram: null,
    stardustRush: null,
    vip: null,
    luckyQuest: null,
    loyalty: null,
  },
  headlinePromo: null,
  isHeadlinePromoDateValid: false,
  pumpkinRush: null,
  isPumpkinRushDateValid: false,
  brandAssets: [],
};

const countBounds = ({ durationFrom, durationTo }: IBrandAsset) =>
  Number(!!durationFrom) + Number(!!durationTo);

/**
 * Picks the brand asset active right now. A bounded (seasonal) asset wins
 * over an always-on one, whatever their order in the CMS.
 */
export const getActiveBrandAsset = (brandAssets: IBrandAsset[] | null) =>
  (brandAssets ?? [])
    .filter(schedule)
    .sort((a, b) => countBounds(b) - countBounds(a))[0] ?? null;
